import { motion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const WHY_IMAGE =
  "https://images.pexels.com/photos/3361471/pexels-photo-3361471.jpeg?auto=compress&cs=tinysrgb&w=1400";

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

const BODY_LINES = [
  "Every weekend, grassroots football writes thousands of stories.",
  "Most are forgotten before the next one begins.",
  "Not because they didn't matter.",
  "But because no one built a home to remember them.",
];

const STRIP = [
  { id: "remembered", top: "Every Player", accent: "Remembered" },
  { id: "preserved", top: "Every Match", accent: "Preserved" },
  { id: "connected", top: "Every Community", accent: "Connected" },
];

export default function WhyWeExist() {
  return (
    <section
      id="why-we-exist"
      data-testid="why-we-exist"
      className="relative z-20 border-t border-[#2A2A2A] bg-[#0B0B0B] py-20 md:py-28"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        {/* Top: copy left, media right — aligned to same top edge */}
        <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-2 lg:gap-16">
          {/* Copy */}
          <div className="order-2 lg:order-1">
            <motion.span
              {...fadeUp}
              transition={{ duration: 0.8, ease: EASE }}
              data-testid="why-we-exist-eyebrow"
              className="mb-7 flex items-center gap-3"
            >
              <span className="h-2 w-2 rounded-full bg-[#1F7A4D]" />
              <span className="font-body text-xs md:text-sm font-medium tracking-[0.3em] uppercase text-[#B3B3B3]">
                Why We Exist
              </span>
            </motion.span>

            <motion.h2
              {...fadeUp}
              transition={{ duration: 0.9, ease: EASE, delay: 0.05 }}
              data-testid="why-we-exist-heading"
              className="font-display uppercase text-white text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-[0.01em]"
            >
              Grassroots Football
              <br />
              Deserves a Home.
            </motion.h2>

            <motion.div
              {...fadeUp}
              transition={{ duration: 0.9, ease: EASE, delay: 0.12 }}
              className="mt-8 max-w-[46ch] space-y-3"
            >
              {BODY_LINES.map((line, i) => (
                <p
                  key={i}
                  className="font-body text-lg md:text-xl font-light leading-snug text-[#B3B3B3]"
                >
                  {line}
                </p>
              ))}
            </motion.div>

            <motion.p
              {...fadeUp}
              transition={{ duration: 1, ease: EASE, delay: 0.18 }}
              data-testid="why-we-exist-final"
              className="mt-12 font-display uppercase text-white text-3xl md:text-4xl lg:text-[2.75rem] leading-[1.02] tracking-[0.015em]"
            >
              That&apos;s why we built The Football Collective.
            </motion.p>
          </div>

          {/* Media */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 1, ease: EASE, delay: 0.1 }}
            className="order-1 lg:order-2"
          >
            <div className="relative aspect-[4/3] w-full overflow-hidden border border-[#2A2A2A]">
              <img
                src={WHY_IMAGE}
                alt="Community football match in play on a grassroots pitch in India"
                data-testid="why-we-exist-image"
                className="h-full w-full object-cover object-center"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-[#0B0B0B]/30" />
            </div>
          </motion.div>
        </div>

        {/* Three-column editorial strip */}
        <div className="mt-16 md:mt-20 border-t border-[#2A2A2A] pt-12 md:pt-16">
          <div className="grid grid-cols-1 sm:grid-cols-3">
            {STRIP.map((item, i) => (
              <motion.div
                key={item.id}
                {...fadeUp}
                transition={{ duration: 0.8, ease: EASE, delay: i * 0.1 }}
                data-testid={`why-line-${item.id}`}
                className="border-t border-[#2A2A2A] py-6 first:border-t-0 sm:border-t-0 sm:border-l sm:first:border-l-0 sm:py-2 sm:px-10 sm:first:pl-0 sm:last:pr-0"
              >
                <p className="font-display uppercase text-white text-3xl md:text-4xl leading-[0.95] tracking-[0.02em]">
                  {item.top}
                </p>
                <p className="font-display uppercase text-[#1F7A4D] text-3xl md:text-4xl leading-[0.95] tracking-[0.02em]">
                  {item.accent}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
